"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { LogIn, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_SHORT_LABELS } from "@/shared/config/i18n/config";
import { useLanguage } from "@/shared/hooks/use-language";
import { LandingLanguageSwitcher } from "./landing-language-switcher";
function smoothScroll(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute("href");
  if (!href?.startsWith("#")) return;
  const el = document.getElementById(href.slice(1));
  if (el) {
    e.preventDefault();
    el.scrollIntoView({
      behavior: "smooth"
    });
  }
}
export function LandingHeader() {
  const $ = _c(59);
  const {
    t
  } = useTranslation();
  const {
    language,
    languages,
    setLanguage
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const onScroll = () => setScrolled(window.scrollY > 20);
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", onScroll);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== t) {
    t2 = t("landing.nav.platform");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      href: "#features",
      label: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("landing.nav.ratings");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = {
      href: "#ratings",
      label: t4
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("landing.nav.data");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = {
      href: "#stats",
      label: t6
    };
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("landing.nav.activity");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = {
      href: "#activity",
      label: t8
    };
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t) {
    t10 = t("landing.nav.download");
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = {
      href: "#download",
      label: t10
    };
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t3 || $[24] !== t5 || $[25] !== t7 || $[26] !== t9) {
    t12 = [t3, t5, t7, t9, t11];
    $[22] = t11;
    $[23] = t3;
    $[24] = t5;
    $[25] = t7;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const navLinks = t12;
  const t13 = `mx-auto flex h-14 max-w-7xl items-center justify-between rounded-2xl border px-4 transition-all duration-300 sm:px-6 ${scrolled ? "border-slate-200/60 bg-white/75 shadow-lg shadow-slate-900/5 backdrop-blur-xl" : "border-white/40 bg-white/50 backdrop-blur-md"}`;
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Link href="/" className="flex items-center gap-2.5"><Image src="/images/logo.svg" alt="PharmaLogic" width={32} height={32} className="size-8 rounded-lg" /><span className="text-lg font-bold tracking-tight text-slate-900">PharmaLogic</span></Link>;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== navLinks) {
    t15 = <nav className="hidden items-center gap-1 md:flex">{navLinks.map(_temp)}</nav>;
    $[29] = navLinks;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LandingLanguageSwitcher />;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <LogIn className="size-3.5" />;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t) {
    t18 = t("landing.nav.login");
    $[33] = t;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = <div className="hidden items-center gap-2 md:flex">{t16}<Link href="/auth/login" className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800">{t17}{t18}</Link></div>;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== mobileOpen) {
    t20 = () => setMobileOpen(!mobileOpen);
    $[37] = mobileOpen;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== mobileOpen) {
    t21 = mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />;
    $[39] = mobileOpen;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21) {
    t22 = <button onClick={t20} className="inline-flex size-11 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:outline-none md:hidden" aria-label="Toggle menu">{t21}</button>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t13 || $[45] !== t15 || $[46] !== t19 || $[47] !== t22) {
    t23 = <div className={t13}>{t14}{t15}{t19}{t22}</div>;
    $[44] = t13;
    $[45] = t15;
    $[46] = t19;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== language || $[50] !== languages || $[51] !== mobileOpen || $[52] !== navLinks || $[53] !== setLanguage || $[54] !== t) {
    t24 = mobileOpen ? <div className="mx-auto mt-2 max-w-7xl rounded-2xl border border-slate-200/60 bg-white/95 px-4 py-4 shadow-lg shadow-slate-900/5 backdrop-blur-xl md:hidden"><nav className="flex flex-col gap-1">{navLinks.map(link_0 => <a key={link_0.href} href={link_0.href} onClick={e => {
          smoothScroll(e);
          setMobileOpen(false);
        }} className="rounded-lg px-4 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-50">{link_0.label}</a>)}<div className="my-2 h-px bg-slate-100" /><div className="flex flex-wrap gap-1.5 px-4 py-2">{languages.map(lang => <button key={lang} onClick={() => void setLanguage(lang)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${lang === language ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>{LANGUAGE_SHORT_LABELS[lang]}</button>)}</div><Link href="/auth/login" className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white" onClick={() => setMobileOpen(false)}><LogIn className="size-3.5" />{t("landing.nav.login")}</Link></nav></div> : null;
    $[49] = language;
    $[50] = languages;
    $[51] = mobileOpen;
    $[52] = navLinks;
    $[53] = setLanguage;
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t23 || $[57] !== t24) {
    t25 = <header className="sticky top-0 z-50 px-4 pt-4">{t23}{t24}</header>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}
function _temp(link) {
  return <a key={link.href} href={link.href} onClick={smoothScroll} className="rounded-lg px-3.5 py-2 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">{link.label}</a>;
}